/**
 * RgbStringVariant - Variante RGB String para Color
 *
 * Representa cor como string rgb()/rgba()
 *
 * @example
 * "rgb(255, 0, 0)"
 * "rgba(255, 0, 0, 0.5)"
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { RGBA, ColorRgbString, ColorFormatOptions } from "../types";
import {
  NAMED_COLORS,
  normalizeRgbChannel,
  normalizeAlpha,
  hslToRgb,
} from "../types";

// Regex patterns
const RGB_REGEX = /^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i;
const RGBA_REGEX =
  /^rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/i;

export class RgbStringVariant extends BaseVariant<ColorRgbString, RGBA> {
  readonly name = "rgb_string";
  readonly type = "string" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: ColorRgbString): RGBA {
    const str = value.trim();

    // Try rgba() first
    let match = str.match(RGBA_REGEX);
    if (match) {
      return {
        r: normalizeRgbChannel(parseInt(match[1], 10)),
        g: normalizeRgbChannel(parseInt(match[2], 10)),
        b: normalizeRgbChannel(parseInt(match[3], 10)),
        a: normalizeAlpha(parseFloat(match[4])),
      };
    }

    // Try rgb()
    match = str.match(RGB_REGEX);
    if (match) {
      return {
        r: normalizeRgbChannel(parseInt(match[1], 10)),
        g: normalizeRgbChannel(parseInt(match[2], 10)),
        b: normalizeRgbChannel(parseInt(match[3], 10)),
        a: 1,
      };
    }

    throw new Error(`Invalid rgb string: ${value}`);
  }

  fromBase(base: RGBA): ColorRgbString {
    const r = normalizeRgbChannel(base.r);
    const g = normalizeRgbChannel(base.g);
    const b = normalizeRgbChannel(base.b);
    const a = normalizeAlpha(base.a);

    if (a < 1) {
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    return `rgb(${r}, ${g}, ${b})`;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): ColorRgbString | null {
    // Already a string
    if (typeof input === "string") {
      const trimmed = input.trim().toLowerCase();

      // Named color
      if (NAMED_COLORS[trimmed]) {
        return this.fromBase(NAMED_COLORS[trimmed]);
      }

      // Already rgb/rgba format
      if (RGB_REGEX.test(trimmed) || RGBA_REGEX.test(trimmed)) {
        try {
          const rgba = this.toBase(trimmed);
          return this.fromBase(rgba);
        } catch {
          return null;
        }
      }

      // Hex format
      const hexMatch = trimmed.match(
        /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i,
      );
      if (hexMatch) {
        return this.fromBase({
          r: parseInt(hexMatch[1], 16),
          g: parseInt(hexMatch[2], 16),
          b: parseInt(hexMatch[3], 16),
          a: hexMatch[4] ? parseInt(hexMatch[4], 16) / 255 : 1,
        });
      }

      // Shorthand hex
      const hexShortMatch = trimmed.match(
        /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i,
      );
      if (hexShortMatch) {
        return this.fromBase({
          r: parseInt(hexShortMatch[1] + hexShortMatch[1], 16),
          g: parseInt(hexShortMatch[2] + hexShortMatch[2], 16),
          b: parseInt(hexShortMatch[3] + hexShortMatch[3], 16),
          a: hexShortMatch[4]
            ? parseInt(hexShortMatch[4] + hexShortMatch[4], 16) / 255
            : 1,
        });
      }

      // hsl()/hsla() string
      const hslMatch = trimmed.match(
        /^hsla?\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (hslMatch) {
        const rgb = hslToRgb(
          parseInt(hslMatch[1], 10),
          parseInt(hslMatch[2], 10),
          parseInt(hslMatch[3], 10),
        );
        return this.fromBase({
          ...rgb,
          a: hslMatch[4] ? parseFloat(hslMatch[4]) : 1,
        });
      }

      return null;
    }

    // RGB object
    if (
      typeof input === "object" &&
      input !== null &&
      "r" in input &&
      "g" in input &&
      "b" in input
    ) {
      const obj = input as { r: number; g: number; b: number; a?: number };
      return this.fromBase({
        r: obj.r,
        g: obj.g,
        b: obj.b,
        a: obj.a ?? 1,
      });
    }

    // HSL object
    if (
      typeof input === "object" &&
      input !== null &&
      "h" in input &&
      "s" in input &&
      "l" in input
    ) {
      const obj = input as { h: number; s: number; l: number; a?: number };
      const rgb = hslToRgb(obj.h, obj.s, obj.l);
      return this.fromBase({
        ...rgb,
        a: obj.a ?? 1,
      });
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: ColorRgbString): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "string") {
      errors.push("RGB string must be a string");
      return { valid: false, errors };
    }

    const str = value.trim();

    if (!RGB_REGEX.test(str) && !RGBA_REGEX.test(str)) {
      errors.push('Invalid format. Use "rgb(r, g, b)" or "rgba(r, g, b, a)"');
      return { valid: false, errors };
    }

    // Parse and validate values
    try {
      const rgba = this.toBase(value);

      if (rgba.r < 0 || rgba.r > 255) {
        errors.push("Red value must be between 0 and 255");
      }
      if (rgba.g < 0 || rgba.g > 255) {
        errors.push("Green value must be between 0 and 255");
      }
      if (rgba.b < 0 || rgba.b > 255) {
        errors.push("Blue value must be between 0 and 255");
      }
      if (rgba.a < 0 || rgba.a > 1) {
        errors.push("Alpha value must be between 0 and 1");
      }
    } catch {
      errors.push("Invalid RGB string format");
    }

    return { valid: errors.length === 0, errors };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: ColorRgbString, options?: FormatOptions): string {
    const opts = options as ColorFormatOptions | undefined;

    try {
      const rgba = this.toBase(value);
      const r = rgba.r;
      const g = rgba.g;
      const b = rgba.b;
      const a = rgba.a;

      if (opts?.includeAlpha || a < 1) {
        if (opts?.compact) {
          return `rgba(${r},${g},${b},${a})`;
        }
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }

      if (opts?.compact) {
        return `rgb(${r},${g},${b})`;
      }
      return `rgb(${r}, ${g}, ${b})`;
    } catch {
      return value;
    }
  }
}
